/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Placement } from '../../placement';
import { Logger } from '../../../logger';
import { isAstAvailable } from '../../../util';
import { IAB_STANDARD_BANNER_SIZES } from '../../placement/constants';
import type { SchEventDispatcher } from './sch/events';

import type { CampaignData, RenderedAdData } from '../../../types/index';
import type { PrebidAdUnit } from '../../../types/prebid';
import type { ApnAdObject, ApnTagObject } from '../../../types/vendor/ast';
import type { SiteConfig } from '../../config';

const logger = Logger.getInstance();
const PARTNER_MEMBER_IDS = [9700];
const IGNORED_ADOMAINS = ['google.com'];
const IGNORED_CREATIVES = [505457714, 270049957];
declare const __INTERACTION_SCRIPT__: string;
const INTERACTION_DETECTION_SCRIPT = __INTERACTION_SCRIPT__;

// Nodals first-party tags always embed a <script> element carrying a
// `data-nodals` attribute. This is the AST-side signal that our own tag was
// returned, since AST events carry no advertiser information.
// Contract: the marker must live on the <script> element itself.
const NODALS_TAG_MARKER_REGEX = /<script\b[^>]*\bdata-nodals\b/i;

class AstPlacement extends Placement {
  private willRenderNativeAd: boolean = false;
  private siteEventDispatcher: SchEventDispatcher | null;
  private nativeImpressionListener: ((event: any) => void) | null = null;
  // AST response uuid of the last render this placement processed, used to
  // distinguish a genuine auction-less refresh render (new ad response) from
  // a duplicate event for a render already handled (e.g. a re-dispatched
  // native impression event for the same ad). Null when the ad object carried
  // no uuid.
  private lastProcessedResponseIdentifier: string | null = null;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
    siteEventDispatcher: SchEventDispatcher | null,
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
    this.siteEventDispatcher = siteEventDispatcher;
    logger.debug(`AST Placement created for ${this.adUnitCode}`);
  }

  protected setSlot(): void {
    if (this.slot) {
      return;
    }
    if (isAstAvailable()) {
      logger.debug(`Determining AST tag for adUnit: ${this.adUnitCode}`);
      try {
        const allTags: Record<string, ApnTagObject> = window.apntag?.getTag() || {};
        const matchingTag = Object.values(allTags).find((tag: ApnTagObject) => {
          logger.debug(
            `Checking AST tag ${tag.invCode} for invCode match against ${this.adUnitCode}`,
          );
          return tag.invCode === this.adUnitCode;
        });
        this.slot = matchingTag || null;
        if (this.slot) {
          logger.info(`AST tag located for ${this.adUnitCode}`);
        }
      } catch (e) {
        logger.error(`Error getting AST tag for ${this.adUnitCode}: ${e}`);
        this.slot = null;
      }
    } else {
      logger.error(
        `AST tag not available, unable to determine slot for adUnit: ${this.adUnitCode}`,
      );
    }
  }

  protected setSupportedSizes(): void {
    const iabSizes = IAB_STANDARD_BANNER_SIZES.map((s) => {
      const [w, h] = s.split('x').map(Number);
      return { width: w, height: h };
    });
    try {
      const apnTag = this.slot as ApnTagObject;
      if (apnTag?.sizes) {
        apnTag.sizes.forEach((size: number[]) => {
          // Only consider sizes that match a standard IAB banner size
          if (iabSizes.some((s) => s.width === size[0] && s.height === size[1])) {
            this.addSupportedSize({ width: size[0], height: size[1] });
          }
        });
      }
    } catch (e) {
      logger.warn(`Error determining AST tag size: ${e}`);
    }
  }

  protected determineVisibility(): void {
    const size = this.getSmallestSupportedSize();
    if (!size || this.slot === null) {
      return;
    }

    const apnTag = this.slot as ApnTagObject;
    const element = document.getElementById(apnTag.targetId);
    if (!element) {
      logger.info(`AST adunit '${this.adUnitCode}' target element '${apnTag.targetId}' not found`);
      return;
    }

    const rect = element.getBoundingClientRect();
    const elementArea = size.width * size.height;

    if (elementArea === 0) {
      return;
    }

    const adLeft = rect.left + (rect.width - size.width) / 2;
    const adTop = rect.top + (rect.height - size.height) / 2;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const visibleWidth = Math.max(
      0,
      Math.min(adLeft + size.width, viewportWidth) - Math.max(adLeft, 0),
    );

    const visibleHeight = Math.max(
      0,
      Math.min(adTop + size.height, viewportHeight) - Math.max(adTop, 0),
    );

    const visibleArea = visibleWidth * visibleHeight;

    if (visibleArea >= elementArea / 2) {
      this.isVisible(true);
    } else {
      this.isVisible(false);
    }
  }

  protected setWrappingDomElement(): void {
    if (this.slot) {
      const apnTag = this.slot as ApnTagObject;
      this.wrappingDomElement = document.getElementById(apnTag.utDivId || apnTag.targetId);
    }
  }

  protected resetStateForNewOpportunity(): void {
    this.willRenderNativeAd = false;
  }

  protected addListeners(): void {
    logger.debug(`Adding AST listeners for placement: ${this.adUnitCode}`);
    this.addApnAdAvailableListener();
    this.addApnAdSlotRenderStartListener();
    this.addViewableImpressionListener();
    this.addPrebidAdRenderedListener();
    this.addNativeImpressionListener();
  }

  protected addAdInteractionListener(): void {
    if (this.slot === null || this.interactionListenerAdded) {
      return;
    }
    logger.info(`Adding ad interaction listener for ${this.adUnitCode}`);
    if (this.willRenderNativeAd) {
      this.addNativePlacementInteractionListener();
    } else {
      this.addIframedAdInteractionListener();
    }
    this.interactionListenerAdded = true;
  }

  private addNativePlacementInteractionListener(): void {
    if (!this.wrappingDomElement) {
      logger.warn(
        `Unable to add native ad interaction listener for ${this.adUnitCode}: wrapping DOM element not found`,
      );
      return;
    }

    this.wrappingDomElement.addEventListener(
      'click',
      () => {
        this.dispatchAdInteraction(true);
      },
      true,
    );

    logger.debug(`Added native click listener for ${this.adUnitCode}`);
  }

  private createAndHandleRenderedAdData(
    adObj: ApnAdObject,
    isPrebidAd: boolean,
    isNodalsTag: boolean = false,
  ): void {
    this.slotRenderedForOpportunity = true;
    this.lastProcessedResponseIdentifier = adObj.uuid ? String(adObj.uuid) : null;
    const creativeId = String(adObj.creativeId);
    const renderedAdData: RenderedAdData = {
      advertiserId: '', // Not available from AST
      creativeId: creativeId,
      isProgrammaticBuy: false,
      isPrebidAd: isPrebidAd,
      isNodalsTag: isNodalsTag,
      responseIdentifier: adObj.uuid,
      size: this.renderedSlotSize,
    };
    this.handleRenderedAdData(renderedAdData);
  }

  private addApnAdAvailableListener(): void {
    if (!this.slot) {
      return;
    }
    try {
      const apnTag = this.slot as ApnTagObject;
      apnTag.on('adAvailable', apnTag.targetId, (adObj: ApnAdObject) => {
        if (this.isFirstPartyAdResponse(adObj)) {
          if (adObj.adType === 'banner' && adObj.banner && adObj.banner.content) {
            const detectionLogic =
              '<scr' + 'ipt>' + INTERACTION_DETECTION_SCRIPT + '</' + 'script>';
            if (!adObj.banner.content.includes(detectionLogic)) {
              adObj.banner.content += detectionLogic;
              logger.debug(`Appended interaction script to banner ad for ${this.adUnitCode}`);
            }
          } else if (adObj.adType === 'native' && adObj.native) {
            this.willRenderNativeAd = true;
          }
        }
      });
    } catch (e) {
      logger.error(`Error adding APN adAvailable listener to ${this.adUnitCode}: ${e}`);
    }
  }

  private addApnAdSlotRenderStartListener(): void {
    if (!this.slot) {
      return;
    }

    try {
      const apnTag = this.slot as ApnTagObject;
      logger.debug(`Registering adSlotRenderStart listener on apntag  for: ${this.adUnitCode}`);

      apnTag.on('adSlotRenderStart', apnTag.targetId, (adObj: ApnAdObject) => {
        // Any render into this slot replaces the previously rendered ad; tear
        // down its listeners, impression context and latches whether or not
        // the incoming ad is one we track (mirrors the GPT handler). Without
        // this, a tracked ad's context would survive a non-first-party render
        // and a later click on the new creative (e.g. via the persistent
        // native wrapper click listener) would be attributed to the old
        // impression.
        this.resetStateForNewRender();
        // A first-party banner render arriving with the per-opportunity latch
        // already consumed is an auction-less refresh (ad-server driven render
        // with no Prebid auction). Mint a placement-local opportunity so the
        // new render's impression and interactions are counted; without one
        // the slot would go dark until the next Prebid auction. Native renders
        // are not minted here — the native impression handler below mints for
        // its own path.
        if (
          this.slotRenderedForOpportunity &&
          adObj.adType === 'banner' &&
          adObj.banner &&
          this.isFirstPartyAdResponse(adObj) &&
          !this.isDuplicateOfProcessedRender(adObj)
        ) {
          this.mintRenderOpportunity();
        }
        if (!this.slotRenderedForOpportunity && this.isFirstPartyAdResponse(adObj)) {
          logger.debug(
            `APN ad render start for: ${adObj.targetId}, handled by placement: ${this.adUnitCode}, creative: ${adObj.creativeId}`,
          );
          let isPrebidAd = false;
          if (adObj.adType === 'banner' && adObj.banner) {
            // Native rendered slot size handled in adAvailable listener
            this.renderedSlotSize = { width: adObj.banner.width, height: adObj.banner.height };
            isPrebidAd = adObj.banner.content.includes('ucTag.renderAd');
            const isNodalsTag = NODALS_TAG_MARKER_REGEX.test(adObj.banner.content);
            this.createAndHandleRenderedAdData(adObj, isPrebidAd, isNodalsTag);
          }
        }
      });
    } catch (e) {
      logger.error(`Error adding APN adSlotRenderStart listener to ${this.adUnitCode}: ${e}`);
    }
  }

  private addViewableImpressionListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      const apnTag = this.slot as ApnTagObject;
      logger.debug(`Registering impressionViewable listener on apntag for: ${this.adUnitCode}`);
      apnTag.on('mrcView', apnTag.targetId, (adObj: ApnAdObject) => {
        logger.debug(
          `impressionViewable dispatched by apntag for: ${adObj.targetId}, ${this.adUnitCode}`,
        );
        this.dispatchViewableImpression();
      });
    } catch (e) {
      logger.error(`Error adding APN viewable impression listener to ${this.adUnitCode}: ${e}`);
    }
  }

  private addNativeImpressionListener(): void {
    if (!this.siteEventDispatcher) {
      return;
    }

    this.nativeImpressionListener = (event: any) => {
      const adObj = event.data;
      const apnTag = this.slot as ApnTagObject;

      if (adObj.targetId !== apnTag.targetId) {
        return;
      }

      // A native render event arriving with the per-opportunity latch already
      // consumed is either an auction-less refresh render (new ad response —
      // mint an opportunity so it is counted) or a duplicate event for the
      // render already processed (same response uuid — leave the latch
      // consumed so the live render's state and interaction listener survive
      // untouched).
      if (
        this.slotRenderedForOpportunity &&
        adObj.native &&
        !this.isDuplicateOfProcessedRender(adObj)
      ) {
        this.mintRenderOpportunity();
      }
      if (!this.slotRenderedForOpportunity) {
        logger.info(
          `Native impression event for ad: ${this.adUnitCode}, targetId: ${adObj.targetId}`,
        );
        if (adObj.native) {
          // See adSlotRenderStart: tear down the previous render's state
          // before recording the new native render.
          this.resetStateForNewRender();
          if (adObj.native.width && adObj.native.height) {
            this.renderedSlotSize = { width: adObj.native.width, height: adObj.native.height };
          } else if (adObj.native.image && adObj.native.image.width && adObj.native.image.height) {
            this.renderedSlotSize = {
              width: adObj.native.image.width,
              height: adObj.native.image.height,
            };
          }
          this.createAndHandleRenderedAdData(adObj, false);
        }
      }
    };
    this.siteEventDispatcher.on('NativeImpression', this.nativeImpressionListener);
  }

  // AST ad responses carry a uuid unique per response, so an event with the
  // uuid of the render already processed is a duplicate dispatch for the same
  // ad (e.g. the publisher's native layer re-announcing an existing render),
  // not a new ad — a legitimate re-serve of the same creative arrives under a
  // fresh uuid and still counts. Events with no uuid cannot be deduplicated
  // and are treated as new renders.
  private isDuplicateOfProcessedRender(adObj: ApnAdObject): boolean {
    return !!adObj.uuid && String(adObj.uuid) === this.lastProcessedResponseIdentifier;
  }

  private isFirstPartyAdResponse(adObj: ApnAdObject): boolean {
    return (
      !IGNORED_ADOMAINS.includes(adObj?.adomain ?? '') &&
      !IGNORED_CREATIVES.includes(adObj.creativeId) &&
      PARTNER_MEMBER_IDS.includes(adObj.buyerMemberId)
    );
  }

  public destroy(): void {
    if (this.nativeImpressionListener && this.siteEventDispatcher) {
      this.siteEventDispatcher.off('NativeImpression', this.nativeImpressionListener);
      this.nativeImpressionListener = null;
    }
    super.destroy();
  }
}

export { AstPlacement };
